"use client"

import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material"
import { useLocale, useTranslations } from "next-intl"
import { LegacyRef, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { Controller, useForm } from "react-hook-form"
import ActiveCheckbox from "ui/icons/ActiveCheckbox"
import CheckboxIcon from "ui/icons/CheckboxIcon"
import { generateSchema, sendCvClick, sendCvError, sendCvSuccess } from "utils"
import { ApplyFormType, postCareerForm } from "utils/api"
import * as yup from "yup"
import Loading from "../Common/Loading"
import { CustomSelect } from "../ContactUs/Forms/Common"

const StyledButton = styled(Button)<{ $isDisabled: boolean }>`
  width: 100%;

  border: ${(p) => (p.$isDisabled ? "1px solid #d1d5db" : "none")};
  color: #051711;
  background-color: ${(p) => (p.$isDisabled ? "transparent" : "#008761")};
`

const StyledWrapperForm = styled.div`
  padding-top: 32px;
`

const StyledInputDivs = styled.div`
  padding-top: 24px;
`

const StyledCheckboxRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  margin-bottom: 44px;
`

const StyledSubtitle = styled(Typography)<{ component?: React.ElementType }>`
  font-size: 24px !important;
  color: #051711;
  font-weight: 700;
  margin: 0px;
  ${(p) => p.theme.breakpoints.up("md")} {
    font-size: 32px !important;
  }
`

const FormContainer = styled.form``

const generatedSchema = generateSchema("he")

type Props = {
  setSubmitForm: any
  setIsFormError: any
  isDriversPage?: boolean
  tooltipText: string
}

const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable.")
}

const Form = ({
  setSubmitForm,
  setIsFormError,
  isDriversPage,
  tooltipText,
}: Props) => {
  const t = useTranslations("Forms")
  const reCaptchaRef = useRef<ReCAPTCHA>(null)
  const [pending, setPending] = useState(false)
  const locale = useLocale()
  const schema = yup.object({
    firstName: generatedSchema.fields.fullname,
    phone: generatedSchema.fields.phonehe,
    preferredWorkRegion: yup.string().required("אנא בחר איזור עבודה מועדף"),
    hasLicense: yup.boolean(),
    complaint: yup.boolean().optional(),
  })
  type FormData = yup.InferType<typeof schema>
  const options = [
    { value: "חיפה", label: locale === "he" ? "חיפה" : "Haifa" },
    {
      value: "העמק (עפולה)",
      label: locale === "he" ? "העמק (עפולה)" : "The Valley (Afula)",
    },
    { value: "כרמיאל", label: locale === "he" ? "כרמיאל" : "Karmiel" },
    { value: "שרון", label: locale === "he" ? "שרון" : "Sharon" },
    { value: "מרכז", label: locale === "he" ? "מרכז" : "Central" },
    {
      value: "אשדוד-אשקלון",
      label: locale === "he" ? "אשדוד-אשקלון" : "Ashdod–Ashkelon",
    },
    { value: "דרום", label: locale === "he" ? "דרום" : "South" },
    {
      value: "ירושלים רבתית",
      label: locale === "he" ? "ירושלים רבתית" : "Greater Jerusalem",
    },
    { value: "אילת", label: locale === "he" ? "אילת" : "Eilat" },
  ]

  const {
    handleSubmit,
    control,
    setValue,
    register,
    formState: { errors, isSubmitted, isValid, isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      firstName: "",
      phone: "",
      preferredWorkRegion: "",
      hasLicense: false,
    },
    resolver: yupResolver(schema),
    mode: "onChange",
  })

  const onSubmit = async (data: FormData) => {
    sendCvClick(
      t("submitCV") || "",
      isDriversPage ? "להיות נהג באגד" : "הכשרה באגד מכללות",
      data.preferredWorkRegion || "",
      isDriversPage ? "Drivers" : "Colleges",
    )
    const gRecaptchaResponse =
      (await reCaptchaRef?.current?.executeAsync()) as string
    reCaptchaRef?.current?.reset()
    setPending(true)
    const applyFormType: ApplyFormType = isDriversPage ? "Driver" : "Collage"
    try {
      const formdata = {
        fullName: data.firstName as string,
        cellular: data.phone as string,
        address: data.preferredWorkRegion,
        hasLicense: data.hasLicense || false,
        complaint: data.complaint || false,
        organization: "",
        language: "he",
        sid: window?.__ctm?.config?.sid || "-1",
        recaptchaResponse: gRecaptchaResponse,
        applyFormType: applyFormType,
      }
      const response = await postCareerForm(formdata)
      if (response && response.body) {
        setSubmitForm(true)
        setIsFormError(false)
        sendCvSuccess(
          t("successMessage") || "",
          isDriversPage ? "להיות נהג באגד" : "הכשרה באגד מכללות",
          data.preferredWorkRegion || "",
          isDriversPage ? "Drivers" : "Colleges",
        )
      } else {
        setSubmitForm(true)
        setIsFormError(true)
        sendCvError(
          t("errorMessage") || "",
          isDriversPage ? "להיות נהג באגד" : "הכשרה באגד מכללות",
          data.preferredWorkRegion || "",
          isDriversPage ? "Drivers" : "Colleges",
        )
      }
    } catch (error) {
      sendCvError(
        t("errorMessage") || "",
        isDriversPage ? "להיות נהג באגד" : "הכשרה באגד מכללות",
        data.preferredWorkRegion || "",
        isDriversPage ? "Drivers" : "Colleges",
      )
      setSubmitForm(true)
      setIsFormError(true)
    } finally {
      setPending(false)
    }
  }
  return (
    <>
      {pending ? (
        <Loading />
      ) : (
        <>
          <StyledSubtitle component="h2">{t("jobApplication")}</StyledSubtitle>
          <FormContainer onSubmit={handleSubmit(onSubmit)}>
            <StyledWrapperForm>
              <Typography variant="body2">
                {t("mandatoryDetails")}
                <span style={{ color: "#E00700" }}>*</span>
              </Typography>
              <StyledInputDivs>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={
                        <>
                          {t("fullName")}
                          <span style={{ color: "#E00700" }}>*</span>
                        </>
                      }
                      type="text"
                      variant="filled"
                      error={!!errors.firstName}
                      ref={register("firstName").ref}
                      helperText={errors.firstName?.message}
                      sx={{
                        marginBlockEnd: "44px",
                      }}
                      fullWidth
                    />
                  )}
                />
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={
                        <>
                          {t("mobileNumber")}
                          <span style={{ color: "#E00700" }}>*</span>
                        </>
                      }
                      type="text"
                      variant="filled"
                      ref={register("phone").ref}
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                      sx={{
                        marginBlockEnd: "44px",
                      }}
                      fullWidth
                    />
                  )}
                />
                <CustomSelect
                  control={control}
                  label={
                    locale === "he"
                      ? "אזור עבודה מועדף"
                      : "Preferred work region"
                  }
                  name="preferredWorkRegion"
                  options={options}
                  required={true}
                  errors={errors}
                  fontSize={16}
                />
                <Controller
                  name="hasLicense"
                  control={control}
                  render={({ field }) => {
                    return (
                      <StyledCheckboxRow>
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              icon={<CheckboxIcon />}
                              checkedIcon={<ActiveCheckbox />}
                              checked={!!field.value}
                              sx={{
                                marginRight: "-8px",
                                ":hover": {
                                  backgroundColor: "transparent",
                                },
                              }}
                              onChange={(e) => {
                                field.onChange(e.target.checked)
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {t("busLicense")}
                            </Typography>
                          }
                          sx={{
                            gap: "8px",
                            marginLeft: 0,
                          }}
                        />
                      </StyledCheckboxRow>
                    )
                  }}
                />
                {/* <MarketingConsent
                  name="complaint"
                  control={control}
                  tooltipText={tooltipText}
                /> */}
              </StyledInputDivs>
              <StyledButton
                variant="primary"
                type="submit"
                $isDisabled={!isValid || isSubmitting}
                disabled={isSubmitting}
              >
                {t("submitCV")}
              </StyledButton>
            </StyledWrapperForm>
          </FormContainer>
          {RECAPTCHA_KEY && (
            <ReCAPTCHA
              ref={reCaptchaRef as LegacyRef<ReCAPTCHA> | undefined}
              sitekey={RECAPTCHA_KEY}
              size="invisible"
              hl="iw"
              tabIndex={-1}
              badge="bottomright"
            />
          )}
        </>
      )}
    </>
  )
}

export default Form
